import clsx from 'clsx';
import React from 'react';
import { Skeleton } from './Feedback';

/**
 * Receipt: the money summary beside a choice — line items as a definition
 * list, one total, then whatever must be agreed and the one primary
 * action. The sticky aside on Plans and Activation and the body of the
 * checkout preview.
 *
 * Amounts are tabular and right-aligned; muted lines are zero or included.
 * At most two numbers are bold on a receipt: the recurring total and, in
 * `ReceiptDue`, what is due today.
 */
export interface ReceiptLine {
  key: string;
  label: React.ReactNode;
  value: React.ReactNode;
  muted?: boolean;
  /** Second, quieter line under the label ("0 min × $0.12"). */
  sub?: React.ReactNode;
}

export const ReceiptLines: React.FC<{
  lines: ReceiptLine[];
  className?: string;
}> = ({ lines, className }) => (
  <dl className={clsx('flex flex-col', className)}>
    {lines.map((l) => (
      <div
        key={l.key}
        className="flex items-baseline justify-between gap-4 border-b border-v2-line/70 py-1.5 text-v2-13 last:border-b-0"
      >
        <dt
          className={clsx('min-w-0', l.muted ? 'text-v2-fg-3' : 'text-v2-fg-2')}
        >
          <span className="block">{l.label}</span>
          {l.sub && (
            <span className="block text-v2-12 text-v2-fg-3">{l.sub}</span>
          )}
        </dt>
        <dd
          className={clsx(
            'v2-num shrink-0 whitespace-nowrap text-right',
            l.muted ? 'text-v2-fg-3' : 'text-v2-fg',
          )}
        >
          {l.value}
        </dd>
      </div>
    ))}
  </dl>
);

export const ReceiptTotal: React.FC<{
  label: React.ReactNode;
  value: React.ReactNode;
  /** One sentence under the total: what happens after today. */
  note?: React.ReactNode;
  className?: string;
}> = ({ label, value, note, className }) => (
  <div className={clsx('border-t border-v2-line pt-3', className)}>
    <div className="flex items-baseline justify-between gap-4">
      <span className="text-v2-13 font-semibold text-v2-fg">{label}</span>
      <span className="v2-num text-v2-20 font-semibold leading-7 tracking-[-0.01em] text-v2-fg">
        {value}
      </span>
    </div>
    {note && (
      <p className="mt-1 text-v2-12 leading-[18px] text-v2-fg-3">{note}</p>
    )}
  </div>
);

/** A sunken well grouping related lines (the plan and its add-ons). */
export const ReceiptGroup: React.FC<{
  children: React.ReactNode;
  className?: string;
}> = ({ children, className }) => (
  <div className={clsx('rounded-v2-lg bg-v2-sunken/70 px-3.5 py-1', className)}>
    {children}
  </div>
);

/**
 * What is due today: an accent-washed panel with the amount and one
 * sentence of what happens after ("Setup fee waived; billing starts …").
 */
export const ReceiptDue: React.FC<{
  label: React.ReactNode;
  value: React.ReactNode;
  note?: React.ReactNode;
  className?: string;
}> = ({ label, value, note, className }) => (
  <div className={clsx('rounded-v2-lg bg-v2-accent/[0.08] p-4', className)}>
    <div className="flex items-baseline justify-between gap-4">
      <span className="text-v2-13 font-semibold text-v2-fg">{label}</span>
      <span className="v2-num text-v2-20 font-semibold leading-7 tracking-[-0.01em] text-v2-fg">
        {value}
      </span>
    </div>
    {note && (
      <p className="mt-1 text-v2-12 leading-[18px] text-v2-fg-2">{note}</p>
    )}
  </div>
);

/** The boxed aside — a resting card. `sticky` pins it near the top on wide screens. */
export const Receipt: React.FC<{
  heading?: React.ReactNode;
  children: React.ReactNode;
  sticky?: boolean;
  className?: string;
  'aria-label'?: string;
}> = ({ heading, children, sticky, className, ...rest }) => (
  <aside
    aria-label={rest['aria-label'] ?? 'Summary'}
    className={clsx(
      'self-start rounded-v2-xl border border-v2-line bg-v2-surface p-5 shadow-v2-card',
      sticky && 'lg:sticky lg:top-4',
      className,
    )}
  >
    {heading && <div className="mb-3">{heading}</div>}
    {children}
  </aside>
);

export const ReceiptSkeleton: React.FC<{ rows?: number }> = ({ rows = 4 }) => (
  <div aria-busy>
    {Array.from({ length: rows }).map((_, i) => (
      <div
        key={i}
        className="flex items-center justify-between border-b border-v2-line/70 py-2 last:border-b-0"
      >
        <Skeleton width={`${40 + ((i * 17) % 30)}%`} className="h-2.5" />
        <Skeleton width={56} className="h-2.5" />
      </div>
    ))}
    <div className="mt-3 flex items-center justify-between border-t border-v2-line pt-3">
      <Skeleton width={72} className="h-3" />
      <Skeleton width={80} className="h-5" />
    </div>
  </div>
);
